import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Props = {
  children: ReactNode;
  /** Stagger delay in ms (applied to root) */
  delay?: number;
  /** Custom translate distance in px (default 12) */
  distance?: number;
  /** When true, wraps direct children with staggered delays */
  stagger?: boolean;
  /** ms between staggered children */
  staggerStep?: number;
  className?: string;
  /**
   * Above-the-fold mode. Renders fully visible from the very first paint —
   * no IntersectionObserver, no opacity/transform gate. Use for the hero
   * and any LCP-critical wrapper to avoid delaying the largest contentful
   * paint behind JS hydration + an IO callback.
   */
  eager?: boolean;
};

/**
 * Subtle scroll-reveal: opacity 0 → 1, translateY(distance) → 0.
 * Uses IntersectionObserver, GPU transforms, respects prefers-reduced-motion.
 */
const Reveal = ({
  children,
  delay = 0,
  distance = 12,
  stagger = false,
  staggerStep = 70,
  className,
  eager = false,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  // When `eager`, start visible so the first paint already shows the
  // content — critical for LCP elements above the fold.
  const [visible, setVisible] = useState(eager);

  useEffect(() => {
    if (eager) return; // already visible — no observer needed
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [eager]);

  const style: CSSProperties = {
    transitionDelay: `${delay}ms`,
    ["--reveal-distance" as string]: `${distance}px`,
  };

  return (
    <div
      ref={ref}
      style={style}
      data-reveal=""
      data-visible={visible ? "true" : "false"}
      className={cn(
        "will-change-[opacity,transform] motion-reduce:transition-none",
        className,
      )}
    >
      {stagger && Array.isArray(children)
        ? children.map((child, i) => (
            <StaggerItem key={i} delay={i * staggerStep} visible={visible}>
              {child}
            </StaggerItem>
          ))
        : children}
    </div>
  );
};

const StaggerItem = ({
  children,
  delay,
  visible,
}: {
  children: ReactNode;
  delay: number;
  visible: boolean;
}) => (
  <div
    data-reveal=""
    data-visible={visible ? "true" : "false"}
    style={{ transitionDelay: `${delay}ms` }}
    className="will-change-[opacity,transform] motion-reduce:transition-none"
  >
    {children}
  </div>
);

export default Reveal;
